import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { downloadFile, isEmpty } from 'components/utils';
import { abortController } from 'store/utils';
import { deleteFileLinkApi, uploadFileApi } from 'store/common/api';
import { attachLayoutsApi, changeUnitCreatorApi, createCompanyApi, createUnitApi, declineResaleUnitApi, deleteCompanyApi, deleteUnitApi, downloadCompanyResaleUnitsApi, editCompanyApi, getCompaniesApi, getCompanyApi, getCompanyResaleUnitsApi, getPublicCompaniesApi, getResaleCompaniesApi, getResaleCompanyUnitsApi, publishResaleUnitApi, regenerateRegistrationTokenApi, registrationRequestApi, updateUnitApi, validateRegistrationUrlApi, } from './api';
const initialState = {
    companies: [],
    publicCompanies: {
        data: [],
        selectedData: [],
        offset: 0,
        total: 0,
        limit: 500,
        hasMore: true,
    },
    company: undefined,
    resaleCompanies: [],
};
const actions = {
    getPublicCompanies: async (payload, { getState, setState }) => {
        try {
            const offset = payload.offset ?? getState().publicCompanies.offset;
            const limit = payload.limit ?? getState().publicCompanies.limit;
            if (offset === 0) {
                setState((state) => {
                    state.publicCompanies.data = [];
                });
            }
            const res = await getPublicCompaniesApi(Object
                .entries({ ...payload, offset, limit })
                .filter((item) => (typeof item[1] === 'object' ? !isEmpty(item[1]) : Boolean(item[1])))
                .reduce((acc, [key, val]) => ({
                ...acc,
                [key]: Array.isArray(val) ? val.join(',') : val,
            }), {}));
            setState((state) => {
                state.publicCompanies.offset = offset + limit;
                state.publicCompanies.data = [...(offset === 0 ? [] : state.publicCompanies.data), ...res.data.data];
                state.publicCompanies.total = res.data.total;
                state.publicCompanies.hasMore = state.publicCompanies.offset < state.publicCompanies.total;
            });
            return res.data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    changePublicCompany: async (company, { setState }) => {
        setState((state) => {
            const index = state.publicCompanies.data.findIndex((item) => item.id === company.id);
            if (index >= 0)
                state.publicCompanies.data[index] = company;
        });
    },
    setSelectedPublicCompanies: async (companyIds, { setState }) => {
        try {
            setState((state) => {
                state.publicCompanies.selectedData = state.publicCompanies.data
                    .filter(({ id }) => companyIds.includes(id))
                    .map(({ id, name, type }) => ({ id, name, type }));
            });
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    toggleSelectedPublicCompany: ({ id, name, type }, { setState }) => {
        setState((state) => {
            const exists = state.publicCompanies.selectedData.some((company) => company.id === id);
            if (exists) {
                state.publicCompanies.selectedData = state.publicCompanies.selectedData
                    .filter((company) => company.id !== id);
            }
            else {
                state.publicCompanies.selectedData.push({ id, name, type });
            }
        });
    },
    resetSelectedPublicCompanies: (_, { setState }) => {
        setState((state) => {
            state.publicCompanies.selectedData = [];
        });
    },
    getCompanies: async (payload, { setState, signal }) => {
        try {
            const { data } = await getCompaniesApi(payload, signal);
            setState((state) => {
                state.companies = data;
            });
            return data;
        }
        catch (err) {
            const error = err;
            throw error.response?.data ?? error;
        }
    },
    getCompany: async (id, { setState }) => {
        try {
            const { data } = await getCompanyApi(id);
            setState((state) => {
                state.company = { ...state.company, ...data };
            });
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    createCompany: async (company, { setState }) => {
        try {
            let id;
            let url;
            if (company?.logo?.file) {
                const form = new FormData();
                form.append('file', company?.logo?.file);
                const { data } = await uploadFileApi(form);
                id = data.fileId;
                url = data.url;
            }
            const { data } = await createCompanyApi({ ...company, logoId: id, logo: url });
            setState((state) => {
                state.companies = [...state.companies, data];
            });
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    editCompany: async (company, { setState }) => {
        try {
            let id;
            let url;
            if (company?.logo?.file) {
                const form = new FormData();
                form.append('file', company?.logo?.file);
                const { data } = await uploadFileApi(form);
                id = data.fileId;
                url = data.url;
            }
            const { data } = await editCompanyApi({ ...company, logoId: id, logo: url });
            setState((state) => {
                const index = state.companies.findIndex((item) => item.id === data.id);
                state.companies[index] = data;
            });
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    deleteCompany: async (companyId, { setState }) => {
        try {
            await deleteCompanyApi(companyId);
            setState((state) => {
                state.companies = state.companies.filter(({ id }) => id !== Number(companyId));
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    getResaleCompanies: async (_, { setState }) => {
        try {
            const { data } = await getResaleCompaniesApi();
            setState((state) => {
                state.resaleCompanies = data;
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    getResaleCompanyUnitsForAdmins: async (companyId, { setState }) => {
        try {
            const { data } = await getResaleCompanyUnitsApi(companyId);
            setState((state) => {
                state.company = {
                    ...(state.company || { name: '' }),
                    units: data?.units,
                };
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    getCompanyResaleUnitsForBrokers: async (_, { setState }) => {
        try {
            const { data } = await getCompanyResaleUnitsApi();
            setState((state) => {
                state.company = {
                    ...(state.company || { name: '' }),
                    units: data,
                };
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    downloadCompanyResaleUnits: async (payload) => {
        try {
            const { data } = await downloadCompanyResaleUnitsApi(payload);
            downloadFile(data, 'resaleUnits.xlsx');
        }
        catch (err) {
            const error = err;
            const data = await new Response(error.response.data).json();
            throw data.message;
        }
    },
    createUnit: async (unit, { setState }) => {
        try {
            const layouts = unit?.files?.layout && await Promise.all(unit.files.layout.map(async (item) => {
                const form = new FormData();
                form.append('file', item.file);
                const res = await uploadFileApi(form);
                return {
                    ...res.data,
                    fileId: res.data.fileId,
                    scope: 'layout',
                    translations: item.translations,
                };
            }));
            const documents = unit?.files?.documents && await Promise.all(unit.files.documents.map(async (item) => {
                const form = new FormData();
                form.append('file', item.file);
                form.append('additionalInfo', JSON.stringify(item.additionalInfo));
                const res = await uploadFileApi(form);
                return {
                    ...res.data,
                    fileId: res.data.fileId,
                    scope: 'documents',
                    description: undefined,
                };
            }));
            const { data } = await createUnitApi({
                ...unit,
                files: {
                    layout: [...(unit?.fiels?.layout || []), ...(layouts || [])].filter((item) => item),
                    documents: [...(unit?.fiels?.documents || []), ...(documents || [])].filter((item) => item),
                },
            });
            setState((state) => {
                if (data.typeOfSale === 'resale')
                    (state.company?.units || []).push(data);
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    updateUnit: async (unit, { setState }) => {
        try {
            const layouts = unit?.files?.layout && await Promise.all(unit.files.layout.map(async (item) => {
                if (item.changeStatus === 'detach') {
                    await deleteFileLinkApi({ pivotId: item.id });
                    return null;
                }
                if (!item.file)
                    return { ...item, translations: item.translations || undefined };
                const form = new FormData();
                form.append('file', item.file);
                URL.revokeObjectURL(item?.url);
                const { data } = await uploadFileApi(form);
                return {
                    ...data,
                    fileId: data.fileId,
                    id: data.id,
                    order: item.order,
                    translations: item.translations,
                    scope: 'layout',
                };
            })).then((res) => res.filter(Boolean));
            const documents = unit?.files?.documents && await Promise.all(unit.files.documents.map(async (item) => {
                if (!item.file)
                    return { ...item, description: item.description || undefined };
                const form = new FormData();
                form.append('file', item.file);
                form.append('additionalInfo', JSON.stringify(item.additionalInfo));
                const res = await uploadFileApi(form);
                return {
                    ...res.data,
                    fileId: res.data.fileId,
                    scope: 'documents',
                    description: undefined,
                };
            }));
            const { data } = await updateUnitApi({
                ...unit,
                files: {
                    ...unit.files,
                    layout: layouts,
                    documents,
                },
            });
            setState((state) => {
                if (state?.company?.units)
                    state.company.units = state.company.units.map((item) => (item.id === data.id ? data : item));
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    publishResaleUnit: async ({ unit }, { setState }) => {
        try {
            const { data } = await publishResaleUnitApi(unit);
            setState((state) => {
                if (state.company?.units)
                    state.company.units = state.company.units.map((item) => (item.id === data.id ? data : item));
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    declineResaleUnit: async ({ unit, reason }, { setState }) => {
        try {
            const { data } = await declineResaleUnitApi({ ...unit, reason });
            setState((state) => {
                if (state.company?.units)
                    state.company.units = state.company.units.map((item) => (item.id === data.id ? data : item));
            });
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    deleteUnit: async (unit, { setState }) => {
        try {
            await deleteUnitApi(unit);
            setState((state) => {
                if (state.company?.units)
                    state.company.units = state.company.units.filter(({ id }) => id !== unit.id);
            });
            return unit;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    attachLayouts: async ({ projectId, data }) => {
        try {
            const layouts = await Promise.all(data.layouts.map(async (item) => {
                const fileIds = await Promise.all(item.files.map(async ({ file }) => {
                    if (!file)
                        return null;
                    const form = new FormData();
                    form.append('file', file);
                    const { data: res } = await uploadFileApi(form);
                    return res.fileId;
                }).filter(Boolean));
                return { fileIds, description: item.description };
            }));
            await attachLayoutsApi({
                projectId,
                data: {
                    unitIds: data.unitIds,
                    layouts,
                },
            });
        }
        catch (err) {
            const error = err;
            throw error.response?.data.message;
        }
    },
    changeUnitCreator: async (payload) => {
        try {
            const { data } = await changeUnitCreatorApi(payload);
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    regenerateRegistrationToken: async (companyId, { setState }) => {
        try {
            const { data } = await regenerateRegistrationTokenApi(companyId);
            setState((state) => {
                state.company = {
                    ...(state.company || {}),
                    ...data,
                };
            });
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data;
        }
    },
    validateRegistrationUrl: async ({ companyId, token }) => {
        try {
            const { data } = await validateRegistrationUrlApi(companyId, token);
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data.message;
        }
    },
    registrationRequest: async (payload) => {
        try {
            let avatarId = '';
            if (payload?.photo?.file) {
                const form = new FormData();
                form.append('file', payload?.photo?.file);
                const { data: { fileId } } = await uploadFileApi(form);
                avatarId = fileId;
            }
            const { data } = await registrationRequestApi({ ...payload, avatarId });
            return data;
        }
        catch (error) {
            // @ts-ignore
            throw error.response.data.errors;
        }
    },
};
const store = { ...initialState, ...actions };
export default create()(devtools(immer((set, get) => abortController(store, set, get, 'Companies')), {
    name: 'Companies',
}));
